import { useMemo, useState } from 'react';
import { FileText, Search } from '@/components/icons';
import { useQuery } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { listGatePasses, type GatePass } from '@/api/gatePass';
import { formatDate } from '@/lib/format';
import { CreateGatePassModal } from './CreateGatePassModal';
import { GatePassDetailModal } from './GatePassDetailModal';

const DELIVERY_TONE: Record<string, 'accent' | 'success' | 'muted' | 'danger'> = {
  'In Transit': 'accent',
  Delivered: 'success',
  Returned: 'muted',
};

export function GatePassPage() {
  const doc = useDocViewer();
  const [search, setSearch] = useState('');
  const [delivery, setDelivery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  const filter = useMemo(
    () => ({
      deliveryStatus: delivery || undefined,
    }),
    [delivery],
  );

  const query = useQuery({
    queryKey: ['gate-passes', filter],
    queryFn: () => listGatePasses(filter),
    refetchInterval: 60_000,
  });
  const data = query.data ?? [];

  return (
    <div>
      <SectionHeader
        title="Gate Pass"      />

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="!pl-5 sm:!pl-8"
              aria-label="Search gate passes"
            />
          </div>
        </FilterField>
        <FilterField label="Delivery">
          <Select value={delivery} onChange={(e) => setDelivery(e.target.value)}>
            <option value="">All</option>
            <option>In Transit</option>
            <option>Delivered</option>
            <option>Returned</option>
          </Select>
        </FilterField>
        {/* Chip lives inside the filter block on both viewports. */}
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="col-span-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:col-span-1 sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
        >
          ＋ Raise Gate Pass
        </button>
      </FilterBar>

      <Table<GatePass>
        storageKey="gate-pass"
        title="Gate Passes"
        count={`${data.length} records`}
        rows={data}
        search={search}
        rowKey={(r) => r.id}
        loading={query.isFetching && data.length === 0}
        columns={[
          {
            key: 'gpNo',
            header: 'GP #',
            cell: (r) => <span className="font-extrabold text-accent">{r.gpNo}</span>,
          },
          { key: 'date', header: 'Date', cell: (r) => formatDate(r.gpDate) },
          {
            key: 'invoice',
            header: 'Invoice',
            cell: (r) => r.invoice?.invoiceNo ?? '—',
          },
          { key: 'party', header: 'Customer', cell: (r) => <span className="font-bold">{r.party?.name ?? '—'}</span> },
          { key: 'vehicle', header: 'Vehicle', cell: (r) => r.vehicleNo ?? '—' },
          { key: 'driver', header: 'Driver', cell: (r) => r.driverName ?? '—' },
          { key: 'lr', header: 'LR #', cell: (r) => r.lrNo ?? '—' },
          { key: 'seal', header: 'Seal #', cell: (r) => r.sealLockNo ?? '—' },
          {
            key: 'returnable',
            header: 'Type',
            cell: (r) =>
              r.isReturnable ? (
                <Badge tone="accent">Returnable</Badge>
              ) : (
                <Badge tone="muted">One-way</Badge>
              ),
          },
          {
            key: 'delivery',
            header: 'Delivery',
            cell: (r) =>
              r.deliveryStatus ? (
                <Badge tone={DELIVERY_TONE[r.deliveryStatus] ?? 'muted'}>
                  {r.deliveryStatus}
                </Badge>
              ) : (
                '—'
              ),
          },
          {
            key: 'status',
            header: 'Status',
            cell: (r) => (
              <Badge tone={r.status === 'Closed' ? 'success' : 'accent'}>{r.status}</Badge>
            ),
          },
          {
            key: 'actions',
            header: '',
            width: '160px',
            cell: (r) => (
              <div className="flex gap-1">
                <Button variant="primary" size="sm" onClick={() => setOpenId(r.id)}>
                  Open
                </Button>
                <Button
                  variant="glass"
                  size="sm"
                  onClick={() => doc.open('gate-pass', r.id, r.gpNo)}
                  title="Open A4 document"
                >
                  <FileText size={14} />
                </Button>
              </div>
            ),
          },
        ]}
      />

      <CreateGatePassModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <GatePassDetailModal
        open={openId !== null}
        gpId={openId}
        onClose={() => setOpenId(null)}
      />
    </div>
  );
}
